import React from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff } from "lucide-react";

function Row({ s, toggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-white border rounded p-3"
    >
      <div className="flex items-center gap-3">
        <button {...attributes} {...listeners} className="text-gray-400 cursor-grab active:cursor-grabbing">
          <GripVertical size={18} />
        </button>
        <div>
          <div className="text-sm font-medium text-gray-800">{s.label}</div>
          <div className="text-xs text-gray-500">key: {s.key}</div>
        </div>
      </div>
      <button
        onClick={() => toggle(s.key)}
        className={`flex items-center gap-2 text-xs px-3 py-1 rounded border transition-colors ${
          s.enabled ? "text-green-700 border-green-300 bg-green-50" : "text-gray-500 border-gray-300 bg-gray-50"
        }`}
      >
        {s.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        {s.enabled ? "Visible" : "Hidden"}
      </button>
    </div>
  );
}

export default function PageBuilder({ content, save }) {
  const sections = (content.sections || []).slice().sort((a, b) => a.order - b.order);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const persist = (arr) => {
    const next = arr.map((s, i) => ({ ...s, order: i + 1 }));
    save({ sections: next });
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.key === active.id);
    const newIdx = sections.findIndex((s) => s.key === over.id);
    persist(arrayMove(sections, oldIdx, newIdx));
  };

  const toggle = (key) => {
    persist(sections.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s)));
  };

  return (
    <div>
      <h2 className="font-medium text-gray-800 mb-2">Page Builder</h2>
      <p className="text-xs text-gray-500 mb-4">
        Drag sections to reorder. Toggle visibility to show/hide a section on the public site.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map((s) => s.key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map((s) => (
              <Row key={s.key} s={s} toggle={toggle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
